import { LANGUAGES, type LanguageOption } from '@/lib/languages'
import type { SupportedLocale } from '@/lib/LocaleContext'

/**
 * F-252: the languages a section or a space can be marked with. A family
 * (`pt`, `en`…) matches every variant of it; a variant (`pt-BR`) matches only
 * itself. Same values the API takes (`src/locales` on the server).
 */
export interface ContentLanguageFamily {
  code: string
  /** The language in itself ("Português"), like the header selector. */
  name: string
  /** The app language a click on "switch to Português" picks. */
  defaultLocale: SupportedLocale
  variants: LanguageOption[]
}

const FAMILY_DEFAULTS: Array<[string, SupportedLocale]> = [
  ['pt', 'pt-BR'],
  ['en', 'en-US'],
  ['es', 'es-ES'],
  ['it', 'it-IT'],
  ['de', 'de-DE'],
  ['fr', 'fr-FR']
]

const familyOf = (code: string) => code.split('-')[0].toLowerCase()

export const CONTENT_LANGUAGE_FAMILIES: ContentLanguageFamily[] = FAMILY_DEFAULTS
  .map(([code, defaultLocale]) => {
    const variants = LANGUAGES.filter((language) => familyOf(language.code) === code)
    return { code, name: variants[0]?.language ?? code.toUpperCase(), defaultLocale, variants }
  })
  .filter((family) => family.variants.length > 0)

const findVariant = (code: string) => LANGUAGES.find((language) => language.code.toLowerCase() === code.toLowerCase())
const findFamily = (code: string) => CONTENT_LANGUAGE_FAMILIES.find((family) => family.code === familyOf(code))

/** "Português" for a family, "Español (México)" for a variant. */
export const contentLanguageLabel = (code: string): string => {
  if (!code.includes('-')) return findFamily(code)?.name ?? code
  const variant = findVariant(code)
  return variant ? `${variant.language} (${variant.country})` : code
}

/** The compact form for a tag: `PT` for a family, `pt-BR` for a variant. */
export const contentLanguageShort = (code: string): string => (code.includes('-') ? code : code.toUpperCase())

/** The flag of a variant, or null for a family (a family has no single country). */
export const contentLanguageFlag = (code: string): string | null => (code.includes('-') ? findVariant(code)?.flag ?? null : null)

/** The app language that opens content marked with `code`. */
export const localeForContentLanguage = (code: string): SupportedLocale | null => {
  if (code.includes('-')) return findVariant(code)?.code ?? null
  return findFamily(code)?.defaultLocale ?? null
}

/** The marked languages in the picker's order (families first, then their variants), for display. */
export const sortContentLanguages = (codes: string[] | null | undefined): string[] => {
  const order = CONTENT_LANGUAGE_FAMILIES.flatMap((family) => [family.code, ...family.variants.map((variant) => variant.code)])
  const rank = (code: string) => {
    const index = order.findIndex((item) => item.toLowerCase() === code.toLowerCase())
    return index === -1 ? order.length : index
  }
  return [...(codes ?? [])].sort((a, b) => rank(a) - rank(b))
}
